import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ToggleLeft, ToggleRight, RefreshCw, Loader2, AlertCircle, ShieldCheck } from 'lucide-react';
import { formatApiErrorDetail } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FLAGS = [
  {
    key: 'registration_enabled',
    label: 'New user registration',
    hint: 'When ON, visitors can submit a registration request (pending admin approval).',
  },
  {
    key: 'password_reset_enabled',
    label: 'Password reset (forgot password)',
    hint: 'When ON, non-admin users can reset their password via the security question.',
  },
];

export const FeatureFlagsPanel = () => {
  const [flags, setFlags] = useState({ registration_enabled: false, password_reset_enabled: false });
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(`${API}/auth/flags`);
      setFlags(res.data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (key) => {
    const next = !flags[key];
    setSavingKey(key);
    setError('');
    try {
      const res = await axios.put(`${API}/admin/feature-flags`, { [key]: next });
      setFlags(res.data);
      setToast(`${key.replace(/_/g, ' ')} → ${next ? 'ENABLED' : 'DISABLED'}`);
      setTimeout(() => setToast(''), 2200);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <section className="glass-card rounded-2xl border border-slate-800 p-6 space-y-5" data-testid="admin-feature-flags">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#ff9e00]" />
          <h3 className="font-display font-bold text-sm text-white uppercase tracking-wide">Feature Flags</h3>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="px-2.5 py-1 rounded border border-[#ff9e00]/40 bg-[#ff9e00]/10 text-[#ff9e00] hover:bg-[#ff9e00]/20 disabled:opacity-50 font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 transition-all cursor-pointer"
          data-testid="btn-flags-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          <span>Sync</span>
        </button>
      </div>

      {toast && (
        <div className="rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 px-3 py-2 text-[11px] font-mono" data-testid="flags-toast">
          {toast}
        </div>
      )}
      {error && (
        <div className="rounded border border-rose-500/30 bg-rose-500/10 text-rose-300 px-3 py-2 text-xs font-mono flex items-start gap-2" data-testid="flags-error">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}

      <div className="space-y-2">
        {FLAGS.map((f) => {
          const enabled = !!flags[f.key];
          const Icon = enabled ? ToggleRight : ToggleLeft;
          return (
            <div
              key={f.key}
              className={`border rounded-lg px-4 py-3 flex items-center justify-between gap-4 transition-colors ${enabled ? 'border-emerald-500/30 bg-emerald-500/[0.04]' : 'border-slate-800 bg-slate-900/30'}`}
              data-testid={`flag-row-${f.key}`}
            >
              <div className="space-y-0.5 min-w-0">
                <div className="text-xs text-slate-200 font-semibold">{f.label}</div>
                <div className="font-mono text-[10px] text-slate-500 leading-snug">{f.hint}</div>
              </div>
              <button
                onClick={() => toggle(f.key)}
                disabled={savingKey === f.key || loading}
                aria-pressed={enabled}
                className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-widest font-bold transition-all cursor-pointer disabled:opacity-50 ${enabled ? 'bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500 hover:text-slate-950' : 'bg-slate-800/60 border border-slate-700 text-slate-300 hover:bg-slate-700'}`}
                data-testid={`btn-flag-${f.key}`}
              >
                {savingKey === f.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-4 h-4" />}
                <span>{enabled ? 'On' : 'Off'}</span>
              </button>
            </div>
          );
        })}
      </div>

      <div className="font-mono text-[10px] text-slate-500 border-t border-slate-900/60 pt-3 leading-relaxed">
        These flags persist in MongoDB (<span className="text-slate-300">feature_flags / global</span>). Each environment (preview / production) holds its own state.
      </div>
    </section>
  );
};
